// # Keyboard Inputs
// (That we care about)

// Returns true when a text input element has keyboard focus, meaning
// keystrokes should go to the input rather than the editor viewport.
export function isTextInputFocused(): boolean {
  const el = document.activeElement;
  return (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    (el instanceof HTMLElement && el.isContentEditable)
  );
}

// True on macOS, where the platform-standard "command" modifier is Cmd
// (metaKey). Other platforms (Windows/Linux) use Ctrl (ctrlKey). Detected
// once from the user agent; guarded so it's safe in non-browser contexts.
export const isMac =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.userAgent);

// Whether the platform-standard "command" modifier is held for this event:
// Cmd on Mac, Ctrl on Windows/Linux. Prefer this over checking metaKey/ctrlKey
// directly for mouse gestures, so we don't bind to Ctrl+click on macOS — where
// the OS already treats Ctrl+click as a secondary (right) click.
export function hasPlatformCmdModifier(e: {
  metaKey: boolean;
  ctrlKey: boolean;
}): boolean {
  return isMac ? e.metaKey : e.ctrlKey;
}

export enum Keys {
  t = "t", // Toggle Theme (handled by IDE command palette)
  Seven = "7", // Toggle Pin Labels
  Eight = "8", // Toggle Instance Name Overlays
  f = "f", // Zoom to Fit
  i = "i", // Add Instance, PortDir::Input
  o = "o", // PortDir::Output
  x = "x", // PortDir::Inout
  p = "p", // Add Pin
  w = "w", // Wire
  r = "r", // Rotate
  R = "R", // Redraw (Shift+R)
  h = "h", // Horizontal flip
  v = "v", // Vertical flip
  z = "z", // Undo / redo (Cmd/Ctrl+Z), Zoom to box (plain Z in idle)
  s = "s", // Save
  q = "q", // Toggle the details editor
  c = "c", // Copy (Cmd/Ctrl+C)
  C = "C", // Cleanup wires (Shift+C)
  l = "l", // Toggle layer info
  u = "u", // Cycle layer up (U in DrawRoute)
  d = "d", // Cycle layer down (D in DrawRoute)
  m = "m", // Measure / Ruler
  e = "e", //
  Comma = ",", // Save
  Escape = "Escape", // Cancel
  Backspace = "Backspace", // Remove
  Delete = "Delete", // Remove
  Enter = "Enter", // Finish
  Space = " ", // Filter this out of names
  T = "T", // Toggle Trackpad Mode (Shift+T)
  a = "a", // Show all layers (when layer panel is open)
  b = "b", // Box-select mode
  B = "B", // Toggle Blockage Visibility (Shift+B)
  g = "g", // Grab / move selection
  G = "G", // Toggle Grid visibility (Shift+G)
  O = "O", // Toggle Outline visibility (Shift+O)
  L = "L", // Layout: Generate From Source (Shift+L, with Cmd/Ctrl)
  Question = "?", // Toggle keyboard help
}
